// AssoAdhesions.jsx — Gestion des adhésions par le bureau (/dashboard/adhesions)
// - Affiche & édite le CODE D'ASSOCIATION (lien d'inscription self-service)
// - Valide / refuse les demandes d'adhésion
// - Enregistre les paiements (chèque / espèces / CB / virement) → adhésion active + n° adhérent
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  Loader2, Copy, Check, Pencil, Users, Clock, Wallet, BadgeCheck,
  X, CreditCard, Banknote, FileText, ArrowLeftRight,
} from 'lucide-react';

function errMsg(e, f = 'Erreur') {
  const d = e?.response?.data?.detail ?? e?.response?.data?.error;
  return typeof d === 'string' ? d : f;
}

const STATUT_META = {
  en_attente_validation: { label: 'À valider', cls: 'bg-amber-100 text-amber-700' },
  validee_attente_paiement: { label: 'À encaisser', cls: 'bg-blue-100 text-blue-700' },
  en_cours_paiement: { label: 'Paiement partiel', cls: 'bg-indigo-100 text-indigo-700' },
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700' },
  refusee: { label: 'Refusée', cls: 'bg-red-100 text-red-700' },
};

export default function AssoAdhesions() {
  const { user } = useAuth();
  const [code, setCode] = useState('');
  const [lien, setLien] = useState('');
  const [editingCode, setEditingCode] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    try {
      const [c, l, s] = await Promise.all([
        api.get('/assos/code-adhesion'),
        api.get('/adhesions-ref'),
        api.get('/adhesions-ref/stats'),
      ]);
      setCode(c.data.code_adhesion); setLien(c.data.lien); setCodeInput(c.data.code_adhesion);
      setList(l.data); setStats(s.data);
    } catch (e) { toast.error(errMsg(e, 'Erreur chargement')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveCode = async () => {
    try {
      const { data } = await api.put('/assos/code-adhesion', { code_adhesion: codeInput });
      setCode(data.code_adhesion); setEditingCode(false); toast.success('Code mis à jour'); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const copy = () => { navigator.clipboard.writeText(lien); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const valider = async (id) => { try { await api.post(`/adhesions-ref/${id}/valider`); toast.success('Demande validée'); load(); } catch (e) { toast.error(errMsg(e)); } };
  const refuser = async (id) => { try { await api.post(`/adhesions-ref/${id}/refuser`, {}); toast.success('Demande refusée'); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (loading) return <div className="p-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;

  const demandes = list.filter((a) => a.statut === 'en_attente_validation');
  const aEncaisser = list.filter((a) => ['validee_attente_paiement', 'en_cours_paiement'].includes(a.statut));
  const actifs = list.filter((a) => a.statut === 'active');

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="adhesions-page">
      <div className="mb-6">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Bureau</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Adhésions</h1>
        <p className="text-sm text-[#475569] mt-2">Partagez votre code, validez les demandes et encaissez les cotisations.</p>
      </div>

      {/* Code d'association */}
      <div className="card mb-6" data-testid="code-asso-card">
        <div className="flex items-center justify-between mb-2">
          <div className="label-uppercase text-[#94A3B8]">Code d'association</div>
          {user?.role === 'president' && !editingCode && (
            <button onClick={() => setEditingCode(true)} className="btn-ghost !p-1.5 text-[#475569]" data-testid="edit-code-btn"><Pencil className="w-4 h-4" /></button>
          )}
        </div>
        {editingCode ? (
          <div className="flex items-center gap-2">
            <input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm font-mono" data-testid="code-input" />
            <button onClick={saveCode} className="btn-primary text-xs" data-testid="save-code-btn">Enregistrer</button>
            <button onClick={() => { setEditingCode(false); setCodeInput(code); }} className="btn-secondary text-xs">Annuler</button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-display text-2xl font-black" style={{ color: 'var(--theme-primary)' }} data-testid="code-value">{code}</span>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 bg-[#F1F5F9] rounded-lg px-3 py-2">
                <span className="text-xs text-[#475569] font-mono truncate flex-1" data-testid="lien-value">{lien}</span>
                <button onClick={copy} className="text-[#475569] hover:text-[#0F172A]" data-testid="copy-lien-btn">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard icon={BadgeCheck} label="Adhérents actifs" value={stats.total_actives} testid="stat-actives" />
          <StatCard icon={Clock} label="À valider" value={stats.en_attente_validation} testid="stat-attente" />
          <StatCard icon={Wallet} label="À encaisser" value={aEncaisser.length} testid="stat-encaisser" />
          <StatCard icon={Users} label="CA cotisations" value={`${stats.ca_total_eur.toFixed(0)} €`} testid="stat-ca" />
        </div>
      )}

      {/* Demandes à valider */}
      <Section title="Demandes à valider" count={demandes.length} testid="section-demandes">
        {demandes.length === 0 ? <Empty text="Aucune demande en attente." /> : demandes.map((a) => (
          <Row key={a.id} a={a}>
            <button onClick={() => valider(a.id)} className="btn-primary text-xs" data-testid={`valider-${a.id}`}>Valider</button>
            <button onClick={() => refuser(a.id)} className="btn-secondary text-xs" data-testid={`refuser-${a.id}`}>Refuser</button>
          </Row>
        ))}
      </Section>

      {/* À encaisser */}
      <Section title="À encaisser" count={aEncaisser.length} testid="section-encaisser">
        {aEncaisser.length === 0 ? <Empty text="Aucun paiement en attente." /> : aEncaisser.map((a) => {
          const paye = (a.paiements || []).reduce((s, p) => s + (p.montant_cts || 0), 0);
          const full = a.formule === 'annual_3x' ? 15000 : a.prix_cts;
          return (
            <Row key={a.id} a={a} extra={`${(paye / 100).toFixed(0)} / ${(full / 100).toFixed(0)} €`}>
              <button onClick={() => setPayFor(a)} className="btn-primary text-xs flex items-center gap-1" data-testid={`payer-${a.id}`}>
                <Wallet className="w-3.5 h-3.5" /> Encaisser
              </button>
            </Row>
          );
        })}
      </Section>

      {/* Adhérents actifs */}
      <Section title="Adhérents actifs" count={actifs.length} testid="section-actifs">
        {actifs.length === 0 ? <Empty text="Aucun adhérent actif." /> : actifs.map((a) => (
          <Row key={a.id} a={a} extra={a.numero_adherent} />
        ))}
      </Section>

      {payFor && <PaymentModal adhesion={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />}
    </div>
  );
}

function StatCard({ icon: Ic, label, value, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <Ic className="w-4 h-4 mb-2" style={{ color: 'var(--theme-primary)' }} />
      <div className="font-display text-2xl font-black text-[#0F172A]">{value}</div>
      <div className="text-[11px] text-[#475569]">{label}</div>
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <div className="mb-6" data-testid={testid}>
      <h2 className="font-display text-lg font-bold text-[#0F172A] mb-3">{title} <span className="text-[#94A3B8] font-normal">({count})</span></h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="card text-center py-8 text-sm text-[#475569]">{text}</div>;
}

function Row({ a, extra, children }) {
  const m = STATUT_META[a.statut] || { label: a.statut, cls: 'bg-slate-100 text-slate-600' };
  return (
    <div className="card flex items-center gap-3 flex-wrap" data-testid={`adhesion-row-${a.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[#0F172A] text-sm truncate">{a.user_nom || a.user_email}</div>
        <div className="text-xs text-[#475569]">{a.label} · {a.user_email}</div>
      </div>
      {extra && <div className="font-mono text-sm text-[#0F172A]">{extra}</div>}
      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full ${m.cls}`}>{m.label}</span>
      {children && <div className="flex gap-2">{children}</div>}
    </div>
  );
}

function PaymentModal({ adhesion, onClose, onDone }) {
  const [mode, setMode] = useState('cheque');
  const [submitting, setSubmitting] = useState(false);
  const modes = [
    { key: 'cheque', label: 'Chèque', icon: FileText },
    { key: 'especes', label: 'Espèces', icon: Banknote },
    { key: 'cb', label: 'Carte (CB)', icon: CreditCard },
    { key: 'virement', label: 'Virement', icon: ArrowLeftRight },
  ];
  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/adhesions-ref/${adhesion.id}/paiement`, { mode });
      toast.success(data.statut === 'active' ? `Adhésion active · N° ${data.numero_adherent || ''}` : 'Paiement partiel enregistré');
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-4 fade-in" onClick={onClose}>
      <div className="card max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold text-[#0F172A]">Encaisser</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#475569] mb-3">{adhesion.user_nom} · {adhesion.label}</p>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {modes.map((mo) => (
            <button key={mo.key} onClick={() => setMode(mo.key)}
              className={`card !p-3 flex items-center gap-2 ${mode === mo.key ? '!border-2' : ''}`}
              style={mode === mo.key ? { borderColor: 'var(--theme-primary)' } : {}}
              data-testid={`mode-${mo.key}`}>
              <mo.icon className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
              <span className="text-sm font-semibold text-[#0F172A]">{mo.label}</span>
            </button>
          ))}
        </div>
        <button onClick={submit} disabled={submitting} className="btn-primary w-full justify-center" data-testid="confirm-payment">
          {submitting ? 'Patientez…' : 'Enregistrer le paiement'}
        </button>
      </div>
    </div>
  );
}
